import { useEffect, useState, useRef } from "preact/hooks";

export const MOUSE_INACTIVITY_TIMEOUT_MS = 5000;
export const FADE_DURATION_MS = 1000;

export interface MouseActivityState {
    isVisible: boolean;
    isAnimating: boolean;
}

/**
 * Hook to track mouse/touch activity and manage visibility timeout
 *
 * @param timeoutMs - Milliseconds of inactivity before starting fade (default: 10000)
 * @param animDurationMs - Duration of animation animation in milliseconds (default: 1000)
 */
export function useMouseActivityTimeout(
    timeoutMs: number = MOUSE_INACTIVITY_TIMEOUT_MS,
    animDurationMs: number = FADE_DURATION_MS
): MouseActivityState {
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);

    const hideTimeoutRef = useRef<number | null>(null);
    const fadeTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        const clearTimeouts = () => {
            if (hideTimeoutRef.current !== null) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            if (fadeTimeoutRef.current !== null) {
                clearTimeout(fadeTimeoutRef.current);
                fadeTimeoutRef.current = null;
            }
        };

        const handleActivity = () => {
            clearTimeouts();

            setIsAnimating(false);
            setIsVisible(true);

            hideTimeoutRef.current = window.setTimeout(() => {
                setIsAnimating(true);

                fadeTimeoutRef.current = window.setTimeout(() => {
                    setIsVisible(false);
                    setIsAnimating(false);
                }, animDurationMs);
            }, timeoutMs);
        };

        handleActivity();

        document.addEventListener('mousemove', handleActivity);
        document.addEventListener('touchstart', handleActivity);
        document.addEventListener('touchmove', handleActivity);

        return () => {
            clearTimeouts();
            document.removeEventListener('mousemove', handleActivity);
            document.removeEventListener('touchstart', handleActivity);
            document.removeEventListener('touchmove', handleActivity);
        };
    }, [timeoutMs, animDurationMs]);

    return { isVisible, isAnimating: isAnimating };
}
